import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import {
  baselinePlanQuery, baselinesQuery, criticalPathQuery, documentsQuery, engagementProfileQuery, meQuery,
  pendingSetsQuery, planQuery, stakeholdersQuery, staleQuery, workItemsQuery,
} from '../api/jedi'
import { BaselineBanner, BaselineControl, changeWords } from '../shell/PlanBaselines'
import { canRecordRelease } from '../shell/EngagementAccess'
import { useEngagement } from '../lib/engagement'
import { todayIn, usePersonZone } from '../lib/personTime'
import { itemsUnderTask } from '../shell/WorkColumns'
import { LoadProblem } from '../shell/LoadProblem'
import { ChatContext } from '../shell/chatContext'
import { RecordSurface } from '../shell/RecordSurface'
import { readNodeOf, readTaskOf } from '../shell/readRecord'
import { useReassign } from '../shell/useReassign'
import { recordSearch, useLinkedRecord } from '../shell/recordLink'
import { PlanFilterBar } from '../shell/PlanFilters'
import { PlanProposals } from '../shell/PlanProposals'
import {
  CheckpointRow, DeliverableRow, LooseHead, MilestoneRow, PhaseRow, TaskRefRow, TaskRow,
} from '../shell/PlanRows'
import {
  NO_FILTERS, categoriesOf, categoryNameIn, narrowing, ownersOf, phaseNameIn, phaseState, phasesOf, placePlan,
  roomWords, servedBy, shortDay,
  taskMatches, taskStatus, tasksIn, type PlacedDeliverable, type PlacedPhase, type PlanFilters,
} from '../shell/planReadings'
import type { CriticalPath, PlanNode } from '../api/types'

export const Route = createFileRoute('/_shell/plan')({
  component: PlanView,
  // A cited record's door (wave 23): the chat's record chip lands here.
  validateSearch: recordSearch,
})

/**
 * THE PLAN, BY PHASE (UX run 08), after the design's own Plan.
 *
 * Phases first, each holding its deliverables, their tasks and the checkpoints
 * that review them; then what has no phase yet; then Timeline, for the
 * milestones and checkpoints no phase holds, with #533's guidance as it
 * shipped. The outcomes and their measures are Outcomes' to show (the routing
 * law); a task names the outcome it feeds instead.
 *
 * Above the plan: J.E.D.I.'s proposals for it, as a reference that points at
 * Home, and the filters. Records stay read-only here (C-033): every row opens
 * its drawer, and Edit plan opens the chat, where a change is stated and
 * reviewed before anything commits.
 */
function PlanView() {
  const { id: engagementId } = useEngagement()
  const plan = useQuery(planQuery(engagementId))
  // Each task's standing reads the work beneath it, as Work's T lens does.
  const items = useQuery(workItemsQuery(engagementId, 'all'))
  const profile = useQuery(engagementProfileQuery(engagementId))
  // The critical path to go-live (RFC-0012's 2026-10-20 amendment, §5), read on the server.
  const critical = useQuery(criticalPathQuery(engagementId))
  // The items whose dates are older than the evidence (RFC-0012's 2026-10-20 amendment, §3).
  const stale = useQuery(staleQuery(engagementId))
  const [staleOnly, setStaleOnly] = useState(false)
  // Named baselines (RFC-0012's 2026-10-20 amendment, §6c): Live, or the plan as one held it.
  const me = useQuery(meQuery)
  const baselines = useQuery(baselinesQuery(engagementId))
  const [view, setView] = useState<string>('live')
  const [changesOnly, setChangesOnly] = useState(false)
  const baselinePlan = useQuery({
    ...baselinePlanQuery(engagementId, view),
    enabled: view !== 'live',
  })
  const sets = useQuery(pendingSetsQuery(engagementId))
  const documents = useQuery(documentsQuery(engagementId))
  const zone = usePersonZone()
  const stakeholders = useQuery(stakeholdersQuery(engagementId))
  const reassign = useReassign(engagementId)
  const { open: editPlan } = useContext(ChatContext)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filters, setFilters] = useState<PlanFilters>(NO_FILTERS)
  // A phase a person opened or closed; the rest open by the rule at rest.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  const nodes = plan.data ?? []
  const allItems = items.data ?? []
  const today = todayIn(zone)
  const phases = phasesOf(profile.data)
  const categories = categoriesOf(profile.data)
  // In a baseline, the plan as it stood then is what is laid out; the drawer still opens today's record.
  const inBaseline = view !== 'live' && baselinePlan.isSuccess
  const shownNodes = inBaseline ? baselinePlan.data.nodes : nodes
  const changes = new Map(inBaseline ? baselinePlan.data.changes.map((c) => [c.nodeId, c]) : [])
  const changedText = (node: PlanNode) => {
    const change = changes.get(node.id)
    if (!change || !inBaseline) return null
    return `Changed since ${baselinePlan.data.baseline.name}: ${changeWords(change, nodes.find((n) => n.id === node.id), phases, today)}`
  }
  const placed = placePlan(shownNodes, phases)
  const tasks = shownNodes.filter((n) => n.nodeType === 'task')

  // While a filter narrows the plan, a phase shows the tasks that match, opens
  // whether or not it was open, and leaves the page when none match.
  const staleItems = new Map(!inBaseline && Array.isArray(stale.data) ? stale.data.map((s) => [s.nodeId, s]) : [])
  const staleWords = (node: PlanNode) => {
    const item = staleItems.get(node.id)
    return item ? `Older than the evidence: ${item.evidence[0]?.words ?? 'later work'}, after its end on ${shortDay(item.end, today)}` : null
  }
  const narrowed = narrowing(filters) || (inBaseline && changesOnly) || (!inBaseline && staleOnly)
  const onPath = new Map((critical.data?.path ?? []).map((t) => [t.id, Number(t.roomDays)]))
  const noPath = critical.data && onPath.size === 0 ? critical.data.why ?? 'There is no critical path yet.' : null
  const matches = (task: PlanNode) => !narrowed
    || ((!filters.critical || onPath.has(task.id))
      && (!(inBaseline && changesOnly) || changes.has(task.id))
      && (inBaseline || !staleOnly || staleItems.has(task.id))
      && taskMatches(task, filters, zone))
  const nameMatches = (node: PlanNode) =>
    !filters.search.trim() || node.name.toLowerCase().includes(filters.search.trim().toLowerCase())

  // At rest every phase with work under way is open. If none is, the first
  // that holds something and is neither complete nor continuous: a plan of
  // deliverables with no tasks yet, which is what cold start leaves, still
  // opens onto something, and never onto a phase that holds nothing.
  const states = new Map(placed.phases.map((p) => [p.id, phaseState(p, allItems)]))
  const underWay = placed.phases.filter((p) => states.get(p.id)?.tone === 'active')
  const restOpen = new Set(
    underWay.length > 0
      ? underWay.map((p) => p.id)
      : placed.phases
        .filter((p) => !p.continuous && holdsAnything(p) && states.get(p.id)?.tone !== 'done')
        .slice(0, 1).map((p) => p.id),
  )
  const isOpen = (id: string) => narrowed || (toggled[id] ?? restOpen.has(id))

  const select = (id: string) => setSelectedId((current) => (current === id ? null : id))

  // A link lands on its record, and opens the phase that holds it.
  const phaseHolding = (id: string) => placed.phases.find((p) =>
    tasksIn(p).some((t) => t.id === id)
    || p.deliverables.some((d) => d.node.id === id)
    || p.timeline.some((n) => n.id === id))?.id
  const { record } = Route.useSearch()
  useLinkedRecord(record, plan.data, (n) => n.id, (n) => {
    setSelectedId(n.id)
    const phase = phaseHolding(n.id)
    if (phase) setToggled((t) => ({ ...t, [phase]: true }))
  })

  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null
  const readRecord = selectedNode === null
    ? null
    : selectedNode.nodeType === 'task'
      ? readTaskForPlan(selectedNode, itemsUnderTask(selectedNode.id, allItems), zone,
        placementOf(selectedNode, nodes, phases, onPath.get(selectedNode.id) ?? null,
          categoryNameIn(categories, selectedNode.categoryId) || null)
          + (staleWords(selectedNode) ? ` ${staleWords(selectedNode)}. Restating its dates clears this.` : ''))
      : readNodeOf(selectedNode, nodes, zone)

  const taskRow = (task: PlanNode, note: string | null = null) => (
    <TaskRow key={task.id} task={task} plan={shownNodes} items={allItems} today={today}
      selected={selectedId === task.id} onSelect={select} note={note}
      roomDays={inBaseline ? null : onPath.get(task.id) ?? null} changed={changedText(task)} stale={staleWords(task)}
      category={categoryNameIn(categories, task.categoryId) || null} />
  )
  // Where a task drawn apart from its deliverables does its good: each one, by phase.
  const forNote = (task: PlanNode) =>
    `For ${servedBy(task, nodes).map((d) => `${d.name} · ${phaseNameIn(phases, d.phaseId)}`).join(', ')}`
  const deliverableBlock = (entry: PlacedDeliverable) => {
    const shown = entry.tasks.filter(matches)
    // The tasks it needs that are drawn in full elsewhere: one line each (ADR-0060's 2026-10-20 amendment).
    const refs = entry.refs.filter((r) => matches(r.task))
    if (narrowed && shown.length === 0 && refs.length === 0) return null
    const rows = (shown.length > 0 || refs.length > 0) && (
      <div className={filters.kinds.deliverables ? 'plan-deliverable-tasks' : 'plan-deliverable-tasks plan-deliverable-bare'}>
        {shown.map((task) => taskRow(task))}
        {refs.map((ref) => (
          <TaskRefRow key={`ref-${ref.task.id}`} entry={ref} items={allItems}
            selected={selectedId === ref.task.id} onSelect={select} />
        ))}
      </div>
    )
    if (!filters.kinds.deliverables) return <div key={entry.node.id}>{rows}</div>
    return (
      <DeliverableRow key={entry.node.id} entry={entry} plan={shownNodes} items={allItems} today={today}
        selected={selectedId === entry.node.id} onSelect={select} changed={changedText(entry.node)}
        stale={staleWords(entry.node)}>
        {rows}
      </DeliverableRow>
    )
  }
  const markerRow = (node: PlanNode) => {
    if (node.nodeType === 'checkpoint' && !filters.kinds.checkpoints) return null
    if (node.nodeType === 'milestone' && !filters.kinds.milestones) return null
    if (!nameMatches(node)) return null
    return node.nodeType === 'checkpoint'
      ? <CheckpointRow key={node.id} node={node} today={today} selected={selectedId === node.id} onSelect={select} />
      : <MilestoneRow key={node.id} node={node} today={today} selected={selectedId === node.id} onSelect={select}
        changed={changedText(node)} />
  }

  const loose = placed.loose.filter(matches)
  const holdsShown = (p: PlacedPhase) => tasksIn(p).some(matches)
  const unphased = placed.unphasedDeliverables.filter((d) => !narrowed || d.tasks.some(matches))
  const visiblePhases = placed.phases.filter((p) => !narrowed || holdsShown(p))
  const nothingMatches = narrowed && tasks.length > 0 && !tasks.some(matches)
  const placedTimeline = placed.phases.some((p) => p.timeline.length > 0)
  const subtitle = [profile.data?.name, plan.isSuccess ? countWords(tasks) : null].filter(Boolean).join(' · ')

  return (
    <section className="surface plan-surface">
      <header className="surface-head plan-head">
        <div>
          <h1>Plan</h1>
          {subtitle && <p className="surface-sub">{subtitle}</p>}
        </div>
        <div className="plan-head-acts">
          {plan.isSuccess && baselines.isSuccess && (
            <BaselineControl engagementId={engagementId} baselines={Array.isArray(baselines.data) ? baselines.data : []} view={view}
              onView={(next) => { setView(next); setChangesOnly(false) }}
              canCapture={canRecordRelease(me.data, engagementId)} zone={zone} />
          )}
          {/* C-033: the only way a plan changes is through J.E.D.I. The door
              opens the chat; nothing commits without approval. It is the
              ratified entry point (6 Aug), which the image leaves out (DP-28). */}
          <button
            type="button"
            className="plan-edit-door plan-edit-live"
            onClick={() => editPlan()}
            title="Every plan change goes through J.E.D.I. State it; nothing commits without your approval."
          >
            Edit plan
          </button>
        </div>
      </header>

      {plan.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={plan.error} />
      {plan.data?.length === 0 && (
        <p className="surface-note">No plan yet for this engagement.</p>
      )}

      {/* J.E.D.I.'s assertion about the plan (nav-attention-counts B-10): it stands until the plan is
          restated, and looking at this page clears nothing. */}
      {!inBaseline && staleItems.size > 0 && (
        <div className="plan-stale-banner" role="status">
          <span className="plan-stale-label">J.E.D.I. asserts</span>
          <span className="plan-stale-words">
            {staleItems.size === 1 ? 'One plan item carries' : `${staleItems.size} plan items carry`} dates older
            than the evidence I hold. Restating {staleItems.size === 1 ? 'its' : 'their'} dates is what clears{' '}
            {staleItems.size === 1 ? 'it' : 'them'}; looking at this page does not.
          </span>
          <button type="button" className={staleOnly ? 'plan-capture plan-view-on' : 'plan-capture'}
            aria-pressed={staleOnly} onClick={() => setStaleOnly(!staleOnly)}>
            {staleItems.size === 1 ? 'Show only the stale item' : 'Show only the stale items'}
          </button>
        </div>
      )}
      {/* A baseline is read-only and about then: J.E.D.I.'s proposals are about now. */}
      {inBaseline && (
        <BaselineBanner plan={baselinePlan.data} changesOnly={changesOnly} onChangesOnly={setChangesOnly} zone={zone} />
      )}
      {plan.isSuccess && sets.isSuccess && !inBaseline && (
        <PlanProposals sets={sets.data} documents={documents.data} />
      )}
      {plan.isSuccess && nodes.length > 0 && (
        <PlanFilterBar filters={filters} owners={ownersOf(nodes)} categories={categories} onChange={setFilters} noPath={noPath} />
      )}
      {/* The path in one line, while the toggle narrows the plan to it. */}
      {filters.critical && critical.data && (
        <p className="plan-path-line" role="status">
          {pathLine(critical.data, today)}
        </p>
      )}

      <div className="plan-layout">
        <div className="plan-main">
          {visiblePhases.map((phase) => {
            const phaseTasks = tasksIn(phase)
            const looseHere = phase.loose.filter(matches)
            const forOthersHere = phase.forOthers.filter(matches)
            return (
              <PhaseRow key={phase.id} phase={phase} open={isOpen(phase.id)}
                onToggle={() => setToggled((t) => ({ ...t, [phase.id]: !isOpen(phase.id) }))}
                items={allItems} today={today}
                shown={narrowed ? phaseTasks.filter(matches).length : null}>
                {/* Every phase the engagement has is drawn (ADR-0061), so one can be empty. */}
                {!holdsAnything(phase) && <p className="plan-phase-empty">Nothing in this phase yet.</p>}
                {phase.deliverables.map(deliverableBlock)}
                {forOthersHere.length > 0 && (
                  <>
                    <LooseHead count={forOthersHere.length} label="For deliverables in other phases" />
                    <div className="plan-loose">{forOthersHere.map((task) => taskRow(task, forNote(task)))}</div>
                  </>
                )}
                {looseHere.length > 0 && (
                  <>
                    <LooseHead count={looseHere.length} />
                    <div className="plan-loose">{looseHere.map((task) => taskRow(task))}</div>
                  </>
                )}
                {phase.timeline.map(markerRow)}
              </PhaseRow>
            )
          })}

          {/* Tasks no deliverable produces and that carry no phase: drawn, never
              dropped (G-26). Every task is to have a phase (Erik, 2026-10-16);
              until one is stated, this is where a task waits. */}
          {loose.length > 0 && (
            <section className="plan-group" aria-label="Serves no single deliverable">
              <LooseHead count={loose.length} />
              <div className="plan-loose">{loose.map((task) => taskRow(task))}</div>
            </section>
          )}

          {/* A deliverable with no phase, which the API refuses at birth
              (ADR-0036 §3): only older records can be here, and they are shown. */}
          {unphased.length > 0 && (
            <section className="plan-group">
              <h2 className="surface-h2">No phase recorded</h2>
              {unphased.map(deliverableBlock)}
            </section>
          )}

          {nothingMatches && <p className="surface-note">No tasks match the current filters.</p>}

          {/* TIMELINE (#533), for the milestones and checkpoints no phase holds:
              a milestone carries no phase, and a checkpoint takes one only from
              a deliverable it reviews. Shown whenever the plan has loaded, so
              an empty one says how to add a dated node. */}
          {plan.isSuccess && (placed.timeline.length > 0 || !placedTimeline) && (
            <article className="plan-outcome plan-timeline">
              <h2 className="surface-h2">Timeline</h2>
              <p className="surface-note timeline-guidance">
                Milestone and checkpoint dates. Task and deliverable dates appear in the plan above.
              </p>
              {placed.timeline.length === 0 && (
                <p className="surface-note timeline-guidance">No milestones or checkpoints yet.</p>
              )}
              {placed.timeline.map(markerRow)}
              {(placed.timeline.length === 0 || placed.timeline.some((node) => !node.date)) && (
                <p className="surface-note timeline-guidance">
                  Use{' '}
                  <button type="button" className="timeline-guidance-action" onClick={() => editPlan()}>
                    Edit plan
                  </button>{' '}
                  to ask J.E.D.I. to {placed.timeline.length === 0 ? 'add a milestone or checkpoint with a date' : 'set the missing date'}.
                  Review the proposal before approving it.
                </p>
              )}
            </article>
          )}
        </div>

        {/*
          The drill-down's `read` body (wave 24). A selected id is a plan node:
          a task, a deliverable, a checkpoint or a milestone. Reassign is offered
          here, and on a plan record it goes to the drafting door with the
          record in scope rather than to the ledger's own.
        */}
        {readRecord && (
          <RecordSurface
            record={readRecord}
            backLabel="Plan"
            reassign={!readRecord.ownable ? undefined : {
              people: (stakeholders.data ?? []).map((p) => p.name),
              busy: reassign.busy,
              says: reassign.says,
              onReassign: (name) =>
                reassign.reassign(readRecord.scopeRef, readRecord.title, name),
            }}
            onClose={() => { setSelectedId(null); reassign.forget() }}
          />
        )}
      </div>
    </section>
  )
}

/** Whether a phase holds any record at all: a deliverable, a task, or a dated node. */
function holdsAnything(phase: PlacedPhase): boolean {
  return phase.deliverables.length > 0 || phase.forOthers.length > 0 || phase.loose.length > 0
    || phase.timeline.length > 0
}

/** "34 tasks", cancelled ones not counted: they are not work that remains or work that was done. */
function countWords(tasks: readonly PlanNode[]): string {
  const n = tasks.filter((t) => (t.state ?? '').toLowerCase() !== 'cancelled').length
  return `${n} task${n === 1 ? '' : 's'}`
}

/**
 * A task's drawer, opened from Plan: it says the same standing as its row,
 * names a status somebody stated where Plan reads that rather than the work,
 * and says where the task sits and every deliverable that needs it.
 */
function readTaskForPlan(task: PlanNode, under: Parameters<typeof taskStatus>[1], zone: string, placement: string) {
  const status = taskStatus(task, under)
  const stated = status.tone === 'blocked' || status.tone === 'other'
    || ((task.state ?? '').toLowerCase().replace(/[\s-]+/g, '_') === 'in_progress')
  return readTaskOf(task, under, zone, undefined,
    { standing: status.word, statedAs: stated ? status.word : null, placement })
}

/** "Critical path to Go-live, 12 Nov: 2 tasks, 1 day late. 1 task before it has no dates." */
function pathLine(path: CriticalPath, today: string): string {
  if (path.path.length === 0) return path.why ?? 'There is no critical path yet.'
  const n = path.path.length
  const unplaced = path.unplaced.length
  return `Critical path to ${path.goLiveName}, ${shortDay(path.goLiveDate, today)}: `
    + `${n} task${n === 1 ? '' : 's'}, ${roomWords(Number(path.roomDays ?? 0))}.`
    + (unplaced > 0 ? ` ${unplaced} open task${unplaced === 1 ? '' : 's'} before it ${unplaced === 1 ? 'has' : 'have'} no dates.` : '')
}

/** "It sits in Prototype, and serves Clinical prototype and Claims data pipeline (Build)." */
function placementOf(
  task: PlanNode, nodes: readonly PlanNode[], phases: Parameters<typeof phaseNameIn>[0], roomDays: number | null,
  category: string | null = null,
): string {
  const own = task.phaseId ? phaseNameIn(phases, task.phaseId) : null
  // Its category, when it has one (ADR-0030's 2026-10-21 amendment): "It is Data work in Build".
  const kind = category ? `It is ${category} work` : null
  const where = own ? (kind ? `${kind} in ${own}` : `It sits in ${own}`) : kind ?? 'It carries no phase of its own yet'
  const serves = servedBy(task, nodes).map((d) =>
    d.phaseId && d.phaseId !== task.phaseId ? `${d.name} (${phaseNameIn(phases, d.phaseId)})` : d.name)
  const path = roomDays === null ? '' : ` It is on the critical path to go-live, ${roomWords(roomDays)}.`
  if (serves.length === 0) return `${where}, and serves no single deliverable.${path}`
  const list = serves.length === 1 ? serves[0] : `${serves.slice(0, -1).join(', ')} and ${serves[serves.length - 1]}`
  return `${where}, and serves ${list}.${path}`
}
